import { z } from "zod";

const envSchema = z.object({
  // Supabase
  VITE_SUPABASE_URL: z.string().url("Supabase URL inválida"),
  VITE_SUPABASE_ANON_KEY: z.string().min(1, "Supabase Anon Key requerida"),

  // AI - Groq (principal)
  VITE_GROQ_API_KEY: z.string().optional(),
  VITE_OPENAI_API_KEY: z.string().optional(),

  // Stripe
  VITE_STRIPE_PUBLIC_KEY: z.string().optional(),
  STRIPE_SECRET_KEY: z.string().optional(),
  STRIPE_WEBHOOK_SECRET: z.string().optional(),

  // App
  VITE_APP_URL: z.string().url().default("http://localhost:5173"),
  VITE_API_URL: z.string().url().optional(),

  // Admin
  VITE_ADMIN_EMAIL: z.string().email().optional(),

  // Sentry (optional - only for production)
  VITE_SENTRY_DSN: z.string().optional(),
  VITE_SENTRY_ENVIRONMENT: z.string().optional().default("production"),
  VITE_SENTRY_TRACES_SAMPLE_RATE: z
    .string()
    .optional()
    .transform((val) => (val ? parseFloat(val) : 0.1))
    .default("0.1"),
  VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE: z
    .string()
    .optional()
    .transform((val) => (val ? parseFloat(val) : 0.1))
    .default("0.1"),
  VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE: z
    .string()
    .optional()
    .transform((val) => (val ? parseFloat(val) : 1.0))
    .default("1.0"),
  SENTRY_AUTH_TOKEN: z.string().optional(),
  SENTRY_ORG: z.string().optional(),
  SENTRY_PROJECT: z.string().optional(),
});

export type Env = z.infer<typeof envSchema>;

// Validate environment variables (call this from your app, not from packages)
export const validateEnv = (envVars: Record<string, any>): Env => {
  try {
    return envSchema.parse(envVars);
  } catch (error) {
    console.error("❌ Invalid environment variables:");
    if (error instanceof z.ZodError) {
      error.errors.forEach((err) => {
        console.error(`  - ${err.path.join(".")}: ${err.message}`);
      });
    }
    throw new Error("Environment validation failed");
  }
};

// Lazy environment getter - only works when called from Vite app
let cachedEnv: Env | null = null;

export const getEnv = (): Env => {
  if (cachedEnv) return cachedEnv;

  // Check if we're in a browser/Vite context
  if (typeof import.meta !== "undefined" && (import.meta as any).env) {
    cachedEnv = validateEnv((import.meta as any).env);
    return cachedEnv;
  }

  throw new Error(
    "Environment variables not available. Make sure to call getEnv() only from the Vite app context.",
  );
};
